import type { IconProps } from "@octopusdeploy/design-system-icons";
import { CodePullRequestIcon, CheckIcon, ClockRotateLeftIcon, DashedCircleIcon, SpinnerIcon, TimeCircleIcon, UserClockIcon, UserMagnifyingGlassIcon, UserXmarkIcon, XmarkIcon, ExclamationCircleIcon } from "@octopusdeploy/design-system-icons";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import type React from "react";
import type { BaseStatuses, BaseStatusSize } from "./BaseStatus";
import { BaseStatus } from "./BaseStatus";
export const taskStatuses = ["queued", "executing", "success", "success-with-warnings", "failed", "canceling", "canceled", "timed-out", "pending-intervention", "no-data", "pending-pull-request-completion"] as const;
export type TaskStatusType = (typeof taskStatuses)[number];
export type TaskStatusSize = BaseStatusSize; // TODO: delete this export if unused after migrating TaskStatusIcons to TaskStatus
export interface TaskStatusProps {
    /**
     * The status of the task.
     */
    status: TaskStatusType;
    /**
     * The size of the status circle.
     */
    size: BaseStatusSize;
    /**
     * Optional tooltip text to display on hover.
     */
    tooltip?: string;
}
export function TaskStatus({ status, size, tooltip }: TaskStatusProps) {
    const { icon, backgroundStatus, iconVariant, label, spinning } = getStatusDetails(status);
    return <BaseStatus icon={icon} status={backgroundStatus} iconVariant={iconVariant} label={label} size={size} spinning={spinning} tooltip={tooltip}/>;
}
interface TaskStatusDetails {
    icon: React.ComponentType<IconProps>;
    backgroundStatus: BaseStatuses;
    iconVariant: "default" | "inverse";
    label: string;
    spinning: boolean;
}
function getStatusDetails(status: TaskStatusType): TaskStatusDetails {
    switch (status) {
        case "queued":
            return {
                icon: TimeCircleIcon,
                backgroundStatus: "neutral",
                iconVariant: "inverse",
                label: "Queued",
                spinning: false,
            };
        case "executing":
            return {
                icon: SpinnerIcon,
                backgroundStatus: "executing",
                iconVariant: "default",
                label: "Executing",
                spinning: true,
            };
        case "failed":
            return {
                icon: XmarkIcon,
                backgroundStatus: "failure",
                iconVariant: "default",
                label: "Failed",
                spinning: false,
            };
        case "canceling":
            return {
                icon: UserClockIcon,
                backgroundStatus: "warning",
                iconVariant: "default",
                label: "Canceling",
                spinning: false,
            };
        case "canceled":
            return {
                icon: UserXmarkIcon,
                backgroundStatus: "warning",
                iconVariant: "default",
                label: "Canceled",
                spinning: false,
            };
        case "timed-out":
            return {
                icon: ClockRotateLeftIcon,
                backgroundStatus: "warning",
                iconVariant: "default",
                label: "Timed out",
                spinning: false,
            };
        case "success":
            return {
                icon: CheckIcon,
                backgroundStatus: "success",
                iconVariant: "default",
                label: "Success",
                spinning: false,
            };
        case "pending-intervention":
            return {
                icon: UserMagnifyingGlassIcon,
                backgroundStatus: "neutral",
                iconVariant: "inverse",
                label: "Pending intervention",
                spinning: false,
            };
        case "success-with-warnings":
            return {
                icon: ExclamationCircleIcon,
                backgroundStatus: "success",
                iconVariant: "default",
                label: "Success with warnings",
                spinning: false,
            };
        case "no-data":
            return {
                icon: DashedCircleIcon,
                backgroundStatus: "neutral",
                iconVariant: "inverse",
                label: "No data",
                spinning: false,
            };
        case "pending-pull-request-completion":
            return {
                icon: CodePullRequestIcon,
                backgroundStatus: "neutral",
                iconVariant: "inverse",
                label: "Pending pull request completion",
                spinning: false,
            };
        default:
            return exhaustiveCheck(status, `Unexpected status ${status} for TaskStatusType`);
    }
}
